import {
  Box,
  Button,
  Container,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ShieldCheck, Mail, Phone, User, FileText } from "lucide-react";
import { motion } from "framer-motion";
import { useState } from "react";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

/* ─────────────────────── INPUT STYLES ─────────────────────────────
   Defined outside the component so the object reference is stable
   and MUI doesn't regenerate class names on every render.
──────────────────────────────────────────────────────────────────── */
const inputStyles = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    background: "rgba(255,255,255,.03)",
    color: "#FFFFFF",
    // clamp keeps the font readable on phones without being oversized on desktop
    fontSize: "clamp(.86rem, 1.1vw, .96rem)",
    transition: "background .2s ease",
    "&:hover": {
      background: "rgba(30,144,255,.03)",
    },
    "&.Mui-focused": {
      background: "rgba(30,144,255,.05)",
    },
    "& fieldset": {
      borderColor: "rgba(30,144,255,.14)",
      transition: "border-color .2s ease",
    },
    "&:hover fieldset": {
      borderColor: "rgba(30,144,255,.35)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#1E90FF",
      borderWidth: "1.5px",
    },
  },
  "& input::placeholder, & textarea::placeholder": {
    color: "rgba(255,255,255,.38)",
    opacity: 1,
    fontSize: "clamp(.84rem, 1.05vw, .94rem)",
  },
  // Prevent adornment icon from being crushed on small screens
  "& .MuiInputAdornment-root": {
    alignSelf: "flex-start",
    mt: "2px",
  },
};

/* ─────────────────────── FEATURE LIST ITEM ────────────────────── */
const FeatureItem = ({ label }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
    <Box sx={{
      width: 8, height: 8,
      flexShrink: 0,
      borderRadius: "50%",
      background: "#1E90FF",
      boxShadow: "0 0 12px rgba(30,144,255,.7)",
    }}/>
    <Typography sx={{
      color: "rgba(255,255,255,.72)",
      fontSize: "clamp(.88rem, 1.1vw, .98rem)",
      lineHeight: 1.5,
    }}>
      {label}
    </Typography>
  </Box>
);

/* ─────────────────────── MAIN EXPORT ──────────────────────────── */
export default function RequestAuditSection() {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", purpose: "" });
  const [loading,  setLoading]  = useState(false);

  const handleChange = (e) =>
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${API_URL}/audit/request/`, formData);
      if (response.data.success) {
        alert("Audit request submitted successfully 🚀");
        setFormData({ name: "", email: "", phone: "", purpose: "" });
      }
    } catch (err) {
      console.error(err);
      alert("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      component="section"
      aria-label="Request AI Audit"
      sx={{
        position: "relative",
        overflow: "hidden",
        background: "#000000",
        // Vertical rhythm: tighter on mobile, generous on wide screens
        pt: { xs: "64px", sm: "80px", md: "100px", lg: "120px", xl: "136px" },
        pb: { xs: "72px", sm: "88px", md: "108px", lg: "128px", xl: "144px" },
      }}
    >
      {/* ── Top-right ambient glow ── */}
      <Box
        component={motion.div}
        animate={{ scale: [1, 1.1, 1], opacity: [0.35, 0.6, 0.35] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        aria-hidden
        sx={{
          position: "absolute",
          // Scales proportionally so it doesn't overwhelm on mobile
          width:  { xs: 200, sm: 300, md: 440, lg: 520, xl: 600 },
          height: { xs: 200, sm: 300, md: 440, lg: 520, xl: 600 },
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(30,144,255,.12), transparent 70%)",
          top: "-15%", right: "-10%",
          filter: "blur(80px)",
          pointerEvents: "none",
        }}
      />

      {/* ── Bottom-left secondary glow ── */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          width:  { xs: 160, sm: 220, md: 320, lg: 380 },
          height: { xs: 160, sm: 220, md: 320, lg: 380 },
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(30,144,255,.06), transparent 70%)",
          bottom: "-10%", left: "-6%",
          filter: "blur(70px)",
          pointerEvents: "none",
        }}
      />

      {/* ── Top separator ── */}
      <Box aria-hidden sx={{
        position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)",
        width: { xs: "80%", md: "60%" }, height: "1px",
        background: "linear-gradient(to right, transparent, rgba(255,255,255,.05), transparent)",
        pointerEvents: "none",
      }}/>

      <Container
        maxWidth="xl"
        sx={{
          position: "relative",
          zIndex: 2,
          px: { xs: "16px", sm: "24px", md: "40px", lg: "56px", xl: "72px" },
          // Hard cap so layout doesn't stretch absurdly on 4K
          maxWidth: { xl: "1440px" },
        }}
      >
        <Grid
          container
          // Gap scales per breakpoint: compact on phones, generous on desktop
          spacing={{ xs: 4, sm: 5, md: 6, lg: 8 }}
          alignItems="center"
        >
          {/* ════════════════ LEFT PANEL ════════════════ */}
          <Grid item xs={12} md={6} lg={5}>
            <Box
              component={motion.div}
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
            >
              {/* ── Badge ── */}
              <Box sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                px: { xs: "12px", md: "16px" },
                py: { xs: "7px",  md: "9px"  },
                borderRadius: "999px",
                background: "rgba(30,144,255,.08)",
                border: "1px solid rgba(30,144,255,.18)",
                mb: { xs: "20px", md: "24px" },
                // Never let badge overflow on tiny screens
                maxWidth: "100%",
              }}>
                <ShieldCheck size={15} color="#63B8FF" style={{ flexShrink: 0 }}/>
                <Typography sx={{
                  color: "#63B8FF",
                  fontSize: "clamp(.66rem, .9vw, .8rem)",
                  fontWeight: 700,
                  letterSpacing: ".08em",
                  textTransform: "uppercase",
                  whiteSpace: "nowrap",
                }}>
                  Enterprise AI Audit
                </Typography>
              </Box>

              {/* ── Heading ── */}
              <Typography
                component="h2"
                sx={{
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                  fontWeight: 800,
                  // Smooth clamp: 1.85rem on 320px → 3.6rem on 1400px+
                  fontSize: "clamp(1.85rem, 4.2vw, 3.6rem)",
                  lineHeight: { xs: 1.1, md: 1.04 },
                  letterSpacing: "clamp(-1px, -0.05em, -2.2px)",
                  color: "rgba(255,255,255,.96)",
                  mb: { xs: "14px", md: "18px" },
                  // Prevent long words from overflowing on very narrow screens
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                Request an
                <br />
                <Box component="span" sx={{ color: "#1E90FF" }}>
                  AI Infrastructure Audit
                </Box>
              </Typography>

              {/* ── Subtitle ── */}
              <Typography sx={{
                maxWidth: { xs: "100%", md: "560px", lg: "600px" },
                color: "rgba(210,230,255,.68)",
                fontSize: "clamp(.88rem, 1.15vw, 1.05rem)",
                lineHeight: 1.88,
                mb: { xs: "28px", sm: "32px", md: "36px" },
              }}>
                Get your AI systems, automation pipelines, infrastructure,
                security, and scalability reviewed by experts. We help
                startups and enterprises identify bottlenecks, compliance
                risks, and optimization opportunities.
              </Typography>

              {/* ── Feature list ── */}
              <Stack spacing={{ xs: "14px", md: "16px" }}>
                {[
                  "AI Architecture Review",
                  "Security & Compliance Audit",
                  "LLM & RAG Optimization",
                  "Scalability Recommendations",
                ].map((item) => (
                  <FeatureItem key={item} label={item}/>
                ))}
              </Stack>
            </Box>
          </Grid>

          {/* ════════════════ RIGHT PANEL ════════════════ */}
          <Grid item xs={12} md={6} lg={7}>
            <Box
              component={motion.div}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              sx={{
                position: "relative",
                borderRadius: { xs: "20px", sm: "24px", md: "28px", lg: "30px" },
                overflow: "hidden",
                p: {
                  xs: "24px 20px",
                  sm: "28px 24px",
                  md: "36px 32px",
                  lg: "44px 40px",
                  xl: "52px 48px",
                },
                background: "rgba(8,8,8,.96)",
                border: "1px solid rgba(30,144,255,.16)",
                backdropFilter: "blur(18px)",
                boxShadow: "0 20px 60px rgba(0,0,0,.45)",
                // Max width on lg+ so the form doesn't become too wide
                maxWidth: { lg: "640px", xl: "680px" },
                ml: { lg: "auto" },
              }}
            >
              {/* ── Card corner glow ── */}
              <Box aria-hidden sx={{
                position: "absolute",
                width: { xs: 140, md: 180, xl: 220 },
                height: { xs: 140, md: 180, xl: 220 },
                borderRadius: "50%",
                background: "radial-gradient(circle, rgba(30,144,255,.14), transparent 70%)",
                top: { xs: -60, md: -80 },
                right: { xs: -60, md: -80 },
                pointerEvents: "none",
              }}/>
              {/* ── Bottom-left card glow ── */}
              <Box aria-hidden sx={{
                position: "absolute",
                width: { xs: 100, md: 140 },
                height: { xs: 100, md: 140 },
                borderRadius: "50%",
                background: "radial-gradient(circle, rgba(30,144,255,.07), transparent 70%)",
                bottom: -40, left: -40,
                pointerEvents: "none",
              }}/>

              {/* ── Card heading ── */}
              <Typography sx={{
                fontFamily: "'Bricolage Grotesque', sans-serif",
                fontWeight: 700,
                color: "#FFFFFF",
                fontSize: "clamp(1.35rem, 2.4vw, 1.95rem)",
                letterSpacing: "-.03em",
                lineHeight: 1.1,
                mb: { xs: "8px", md: "10px" },
              }}>
                Request Audit
              </Typography>

              <Typography sx={{
                color: "rgba(210,230,255,.6)",
                lineHeight: 1.8,
                mb: { xs: "24px", sm: "28px", md: "32px" },
                fontSize: "clamp(.84rem, 1.05vw, .96rem)",
                maxWidth: "480px",
              }}>
                Fill in your details and our AI audit specialists will
                contact you shortly.
              </Typography>

              {/* ── Form ── */}
              <Stack spacing={{ xs: "16px", sm: "18px", md: "20px" }}>

                {/* Name */}
                <TextField
                  fullWidth
                  name="name"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={handleChange}
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <User size={17} color="#63B8FF" style={{ marginRight: 10, flexShrink: 0 }}/>
                    ),
                  }}
                  sx={inputStyles}
                />

                {/* Email */}
                <TextField
                  fullWidth
                  name="email"
                  type="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <Mail size={17} color="#63B8FF" style={{ marginRight: 10, flexShrink: 0 }}/>
                    ),
                  }}
                  sx={inputStyles}
                />

                {/* Phone */}
                <TextField
                  fullWidth
                  name="phone"
                  type="tel"
                  placeholder="Phone Number"
                  value={formData.phone}
                  onChange={handleChange}
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <Phone size={17} color="#63B8FF" style={{ marginRight: 10, flexShrink: 0 }}/>
                    ),
                  }}
                  sx={inputStyles}
                />

                {/* Purpose — multiline */}
                <TextField
                  fullWidth
                  multiline
                  // 4 rows on mobile (saves vertical space), 5 on desktop
                  rows={4}
                  name="purpose"
                  placeholder="Tell us about your AI systems, infrastructure, or audit requirements..."
                  value={formData.purpose}
                  onChange={handleChange}
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <FileText
                        size={17}
                        color="#63B8FF"
                        style={{ marginRight: 10, marginTop: 6, flexShrink: 0, alignSelf: "flex-start" }}
                      />
                    ),
                  }}
                  sx={{
                    ...inputStyles,
                    // Prevent horizontal scroll on tiny screens
                    "& textarea": { wordBreak: "break-word", overflowWrap: "break-word" },
                  }}
                />

                {/* Submit button */}
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={handleSubmit}
                  disabled={loading}
                  component={motion.button}
                  whileHover={!loading ? { scale: 1.02 } : {}}
                  whileTap={!loading  ? { scale: 0.98 } : {}}
                  sx={{
                    mt: { xs: "4px", md: "8px" },
                    py: { xs: "13px", md: "14px" },
                    borderRadius: "13px",
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "clamp(.88rem, 1.1vw, .98rem)",
                    letterSpacing: ".01em",
                    background: "linear-gradient(90deg, #1E90FF 0%, #42A5FF 100%)",
                    boxShadow: "0 12px 35px rgba(30,144,255,.28)",
                    transition: "box-shadow .22s ease",
                    "&:hover": {
                      background: "linear-gradient(90deg, #42A5FF 0%, #1E90FF 100%)",
                      boxShadow: "0 16px 44px rgba(30,144,255,.4)",
                    },
                    "&.Mui-disabled": {
                      background: "rgba(30,144,255,.25)",
                      color: "rgba(255,255,255,.4)",
                    },
                  }}
                >
                  {loading ? "Submitting…" : "Request Audit"}
                </Button>

              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* ── Bottom separator ── */}
      <Box aria-hidden sx={{
        position: "absolute", bottom: 0, left: "50%", transform: "translateX(-50%)",
        width: { xs: "80%", md: "60%" }, height: "1px",
        background: "linear-gradient(to right, transparent, rgba(255,255,255,.04), transparent)",
        pointerEvents: "none",
      }}/>
    </Box>
  );
}